/* eslint-disable sort-keys */
import { BillingsParams } from 'models/Billings'
import { constructorCustomer, CustomerParams } from 'models/Customers'
import { constructorDiscounts, DiscountParams } from 'models/Discounts'
import {
  CancellationReason,
  constructorCancellationReason,
  constructorNfseScheduled,
  InvoiceItem,
  Negativation
} from 'models/Invoice'
import { NfseScheduled, IssueMode, MonthOffset } from 'models/NFe'
import { PaymentTypeDetails } from 'models/PaymentTypeDetails'
import { constructorProducts, Products } from 'models/Plans'
import {
  constructorCreditCards,
  RecurrenceCardParams
} from 'models/RecurrenceCard'
interface Historic {
  event: string
  description: string
  date_change: string
  user: string
}

export interface AdjustmentScheduled {
  uuid: string
  referenceable_type: string
  first_adjustment_date: string
  next_adjustment_date: string
  frequency: 'quarterly' | 'semester' | 'annual'
  adjustment_type: 'customized' | 'ipca' | 'igpm'
  percentage_value: string | null
  automatic_application: boolean
  can_apply_adjustment: boolean
  // null = reajuste vale para todos os itens da recorrência
  billing_item_uuids: string[] | null
  applies_to_all_items: boolean
  created_at: string
  updated_at: string
}
export interface RecurrencesParams {
  uuid: string
  status: string
  frequency: string
  frequency_type?: 'days' | 'weeks' | 'months' | 'years'
  frequency_number?: number
  /** Rótulo já derivado do par canônico pelo backend (Recurrence#frequency_description). */
  frequency_description?: string
  totalCycles: number
  dueDay: number
  firstDueDate: string
  amount: string
  /** Valor do ciclo corrente (Recurrence#cycle_amount). `amount` é o total da composição. */
  cycleAmount?: string
  interestPolicy: boolean
  discountDays: number
  discountAmount: string
  discounts: DiscountParams[]
  recurrenceCards: RecurrenceCardParams[]
  description: string
  createdAt: string
  updatedAt: string
  paymentType: string
  /** `limit_days_for_payment` — nulo quando a recorrência não define um limite próprio. */
  expirationField: number | null
  discountPolicy: boolean
  currentCycle: number
  customer: CustomerParams
  billings: BillingsParams[]
  customerUuid: string
  merchantUuid: string
  cancellationReason: CancellationReason
  recurrencePlan?: Products
  historic?: Historic[]
  interestFee: string
  interestFine: string
  nfsesScheduled?: NfseScheduled
  nfse_issuer_uuid?: string
  nfse_uuid?: string
  nfsePolicy?: string
  metadata: any
  negativation: Negativation
  issue_days?: number
  issue_billing_type?: 'days_before_due_date' | 'specific_day'
  // New fields for interval type configuration
  interval_type?: string // Legacy field - not used in current implementation
  interval_value?: number
  // New fields for specific day NF configuration
  issue_mode?: IssueMode
  specific_day?: number // 1-31
  specific_month_offset?: MonthOffset
  due_date_period?: string // For backward compatibility
  hasBonus?: boolean
  bonusSettings?: {
    cyclesCount: number
    type: 'value' | 'percentage'
    amount: string
    startCycle?: number
  }
  items: InvoiceItem[]
  tags?: string
  paymentTypeDetails?: PaymentTypeDetails
  adjustmentScheduled?: AdjustmentScheduled
  dirtyPendingBillingsCount?: number
  dirtyFutureBillingsCount?: number
  couponUsage?: {
    uuid: string
    couponUuid: string
    couponCode: string
    discountType: string
    discountValue: number
    appliedDiscountAmount: number
    removedAt: string | null
    createdAt: string
  } | null
  hasPaidBillings?: boolean
  hasFutureCycles?: boolean
  // Saldo de bonificação da entrada proporcional (SC-11262). null quando nunca houve saldo.
  bonusCarryover?: RecurrenceBonusCarryover | null
}

export interface RecurrenceBonusCarryover {
  initial: number
  applied: number
  planned: number
  remaining: number
  forfeited: number
  unused: number
  valid_until_cycle: number
  expired: boolean
  by_cycle: { cycle: number; amount: number; status: 'planned' | 'applied' }[]
}

export function constructorRecurrences(data: any) {
  const billings: BillingsParams[] = data?.billings?.length
    ? data?.billings.map((billing?: any) => {
        return {
          antecipated: billing?.antecipated,
          amountPaid: billing?.amount_paid,
          amountBilled: billing?.amount_billed,
          createdAt: billing?.created_at,
          customerUuid: billing?.customer_uuid,
          datePaid: billing?.date_paid,
          description: billing?.description,
          dirty: billing?.dirty,
          discountAmount: billing?.discount_amount,
          discountDays: billing?.discount_days,
          discountPolicy: billing?.discount_policy,
          dueDate: billing?.due_date,
          interestPolicy: billing?.interest_policy,
          merchantUuid: billing?.merchant_uuid,
          paymentType: billing?.payment_type,
          recurrenceCycle: billing?.recurrence_cycle,
          status: billing?.status,
          updatedAt: billing?.updated_at,
          uuid: billing?.uuid,
          recurrence_cards: billing?.recurrence_cards,
          negativation: billing?.negativation,
          installment: billing?.installment
            ? {
                uuid: billing.installment.uuid,
                total_cycles: billing.installment.total_cycles,
                payment_type: billing.installment.payment_type,
                amount: billing.installment.amount,
                created_at: billing.installment.created_at,
                updated_at: billing.installment.updated_at
              }
            : undefined,
          contaAzulInfo: billing?.conta_azul_info
            ? {
                uuid: billing.conta_azul_info.uuid,
                status: billing.conta_azul_info.status,
                caExternalId: billing.conta_azul_info.ca_external || null,
                caProtocolId: billing.conta_azul_info.ca_protocol || null,
                caInstallmentId: billing.conta_azul_info.ca_installment || null,
                caBaixaId: billing.conta_azul_info.ca_baixa || null,
                lastError: billing.conta_azul_info.last_error || null,
                syncedAt: billing.conta_azul_info.synced_at || null
              }
            : null
        }
      })
    : []
  return {
    billings,
    uuid: data?.uuid,
    status: data?.status,
    frequency: data?.frequency,
    frequency_type: data?.frequency_type,
    frequency_number: data?.frequency_number,
    frequency_description: data?.frequency_description,
    totalCycles: data?.total_cycles,
    dueDay: data?.due_day,
    firstDueDate: data?.first_due_date,
    amount: data?.amount,
    cycleAmount: data?.cycle_amount,
    interestPolicy: data?.interest_policy === 'no_interest' ? false : true,
    discounts: data?.discounts ? constructorDiscounts(data?.discounts) : [],
    discountDays: data?.discount_days,
    recurrenceCards: data?.recurrence_cards
      ? constructorCreditCards(data?.recurrence_cards)
      : [],
    cancellationReason: constructorCancellationReason(
      data?.cancellation_reason
    ),
    discountAmount: data?.discount_amount,
    description: data?.description,
    createdAt: data?.created_at,
    updatedAt: data?.updated_at,
    paymentType: data?.payment_type,
    discountPolicy: data?.discount_policy === 'no_discount' ? false : true,
    nfsePolicy: data?.nfse_policy,
    currentCycle: data?.current_cycle,
    customer: data?.customer ? constructorCustomer(data?.customer) : [],
    expirationField: data.limit_days_for_payment,
    customerUuid: data?.customer_uuid,
    merchantUuid: data?.merchant_uuid,
    recurrencePlan: data?.product
      ? constructorProducts([data?.product])[0]
      : undefined,
    negativation: data.negativation,
    historic: data?.historic,
    interestFee: data?.interest_fee,
    interestFine: data?.interest_fine,
    nfsesScheduled: data?.nfse_scheduled
      ? constructorNfseScheduled(data?.nfse_scheduled)
      : undefined,
    nfse_issuer_uuid: data?.nfse_issuer_uuid,
    nfse_uuid: data?.nfse_uuid,
    metadata: data?.metadata,
    hasBonus:
      data?.bonus_policy !== 'no_bonus' &&
      data?.bonus_policy !== undefined &&
      data?.bonus_policy !== null,
    bonusSettings: (() => {
      return data?.bonus_policy !== 'no_bonus' &&
        data?.bonus_policy !== undefined &&
        data?.bonus_policy !== null &&
        data?.bonus
        ? {
            cyclesCount: data.bonus.total_cycles || 1,
            type: data?.bonus_policy === 'fixed' ? 'value' : 'percentage',
            amount: data.bonus.amount ? data.bonus.amount.toString() : '',
            startCycle: data.bonus.start_cycle || 1
          }
        : {
            cyclesCount: 1,
            type: 'value',
            amount: ''
          }
    })(),
    items:
      data.billing_items && Array.isArray(data.billing_items)
        ? data.billing_items.map((item: any) => ({
            uuid: item.uuid,
            quantity: Number(item.quantity),
            ...(item.product
              ? { product: item.product }
              : item.description
              ? { description: item.description }
              : {}),
            amount_billed: item.amount_billed,
            amount_unit: item.amount_unit,
            tags: item.tags,
            start_cycle: item.start_cycle,
            total_cycles: item.total_cycles,
            // Sem o passo, a composição assume 1 e calcula um fim errado: um
            // item trimestral de 5 cobranças a partir do ciclo 3 virava
            // "Ciclos 3 a 7" em vez de terminar no 15.
            cycle_interval: item.cycle_interval,
            bonus_policy: item.bonus_policy,
            bonus_amount: item.bonus_amount,
            bonus_start_cycle: item.bonus_start_cycle,
            bonus_total_cycles: item.bonus_total_cycles,
            metric_kind: item.metric_kind
          }))
        : [],
    paymentTypeDetails: data?.payment_type_details,
    issue_days: data?.issue_days,
    issue_billing_type: data?.issue_billing_type,
    // Map new interval type configuration fields
    interval_type: data?.interval_type,
    interval_value: data?.interval_value,
    // Map new NF configuration fields
    issue_mode: data?.issue_mode,
    specific_day: data?.specific_day,
    specific_month_offset: data?.specific_month_offset,
    due_date_period: data?.due_date_period,
    tags: data?.tags,
    dirtyPendingBillingsCount: data?.dirty_pending_billings_count ?? 0,
    dirtyFutureBillingsCount: data?.dirty_future_billings_count ?? 0,
    hasPaidBillings: data?.has_paid_billings ?? false,
    hasFutureCycles: data?.has_future_cycles ?? true,
    bonusCarryover: data?.bonus_carryover ?? null,
    adjustmentScheduled: data?.adjustment_scheduled
      ? {
          uuid: data.adjustment_scheduled.uuid,
          referenceable_type: data.adjustment_scheduled.referenceable_type,
          first_adjustment_date:
            data.adjustment_scheduled.first_adjustment_date,
          next_adjustment_date: data.adjustment_scheduled.next_adjustment_date,
          frequency: data.adjustment_scheduled.frequency,
          adjustment_type: data.adjustment_scheduled.adjustment_type,
          percentage_value: data.adjustment_scheduled.percentage_value,
          automatic_application:
            data.adjustment_scheduled.automatic_application,
          can_apply_adjustment:
            data.adjustment_scheduled.can_apply_adjustment ?? false,
          billing_item_uuids:
            data.adjustment_scheduled.billing_item_uuids ?? null,
          applies_to_all_items:
            data.adjustment_scheduled.applies_to_all_items ?? true,
          created_at: data.adjustment_scheduled.created_at,
          updated_at: data.adjustment_scheduled.updated_at
        }
      : undefined,
    couponUsage: (() => {
      const usage = data?.coupon_usages?.find((u: any) => !u.removed_at)
      return usage
        ? {
            uuid: usage.uuid,
            couponUuid: usage.coupon?.uuid,
            couponCode: usage.coupon?.code,
            discountType: usage.discount_type_snapshot,
            discountValue: usage.discount_value_snapshot,
            appliedDiscountAmount: usage.applied_discount_amount,
            removedAt: usage.removed_at,
            createdAt: usage.created_at
          }
        : null
    })()
  } as RecurrencesParams
}
